import { useCallback, useRef, useState } from 'react';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
/** Fraction of the plate the network must keep on screen, so it can't be lost. */
const KEEP_VISIBLE = 0.25;

interface View {
  scale: number;
  x: number;
  y: number;
}

interface Point {
  x: number;
  y: number;
}

/**
 * Pan and pinch-zoom for the schematic. Without it the 121-station network is
 * not readable at 390px, and the labels this uncovers are the whole point of
 * the map. Implemented on pointer events so one code path covers touch and
 * mouse, and so iOS Safari's page zoom stays out of it.
 *
 * All arithmetic happens in SVG user units. Pointer events arrive in CSS pixels
 * and the viewBox is scaled to fit the plate, so client deltas are converted
 * through the SVG's own screen matrix — applying them raw moved the map several
 * times the distance the finger travelled.
 */
export function usePanZoom(svgRef: React.RefObject<SVGSVGElement | null>, viewBox: { width: number; height: number }) {
  const [view, setView] = useState<View>({ scale: 1, x: 0, y: 0 });
  const pointers = useRef(new Map<number, Point>());
  const pinchStart = useRef<{ dist: number; scale: number; c: Point; x: number; y: number } | null>(null);
  const viewRef = useRef(view);
  viewRef.current = view;

  /** Client (CSS px) -> SVG user units, excluding this hook's own transform. */
  const toUser = useCallback(
    (clientX: number, clientY: number): Point => {
      const svg = svgRef.current;
      const ctm = svg?.getScreenCTM();
      if (!svg || !ctm) return { x: clientX, y: clientY };
      const pt = new DOMPoint(clientX, clientY).matrixTransform(ctm.inverse());
      return { x: pt.x, y: pt.y };
    },
    [svgRef],
  );

  /** Keeps at least a quarter of the network on the plate in each direction. */
  const clamp = useCallback(
    (v: View): View => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale));
      const limitX = viewBox.width * scale - viewBox.width * KEEP_VISIBLE;
      const limitY = viewBox.height * scale - viewBox.height * KEEP_VISIBLE;
      return {
        scale,
        x: Math.min(viewBox.width * (1 - KEEP_VISIBLE), Math.max(-limitX, v.x)),
        y: Math.min(viewBox.height * (1 - KEEP_VISIBLE), Math.max(-limitY, v.y)),
      };
    },
    [viewBox.width, viewBox.height],
  );

  const onPointerDown = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    pinchStart.current = null;
  }, []);

  const onPointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      const prev = pointers.current.get(e.pointerId);
      if (!prev) return;
      const next = { x: e.clientX, y: e.clientY };
      pointers.current.set(e.pointerId, next);

      const pts = [...pointers.current.values()];
      if (pts.length === 1) {
        const a = toUser(prev.x, prev.y);
        const b = toUser(next.x, next.y);
        setView((v) => clamp({ ...v, x: v.x + (b.x - a.x), y: v.y + (b.y - a.y) }));
        return;
      }
      const [p1, p2] = pts as [Point, Point];
      const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
      const c = toUser((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
      if (!pinchStart.current) {
        const v = viewRef.current;
        pinchStart.current = { dist, scale: v.scale, c, x: v.x, y: v.y };
        return;
      }
      const start = pinchStart.current;
      const ratio = dist / (start.dist || 1);
      setView(() => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, start.scale * ratio));
        const applied = scale / start.scale;
        // Keep the point between the fingers under the fingers.
        return clamp({
          scale,
          x: start.c.x - (start.c.x - start.x) * applied + (c.x - start.c.x),
          y: start.c.y - (start.c.y - start.y) * applied + (c.y - start.c.y),
        });
      });
    },
    [toUser, clamp],
  );

  const releasePointer = useCallback((e: React.PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchStart.current = null;
  }, []);

  const onWheel = useCallback(
    (e: React.WheelEvent<SVGSVGElement>) => {
      const factor = Math.exp(-e.deltaY / 400);
      const p = toUser(e.clientX, e.clientY);
      setView((v) => {
        const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, v.scale * factor));
        const applied = scale / v.scale;
        return clamp({ scale, x: p.x - (p.x - v.x) * applied, y: p.y - (p.y - v.y) * applied });
      });
    },
    [toUser, clamp],
  );

  const reset = useCallback(() => setView({ scale: 1, x: 0, y: 0 }), []);

  return {
    transform: `translate(${view.x} ${view.y}) scale(${view.scale})`,
    scale: view.scale,
    reset,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: releasePointer,
      onPointerCancel: releasePointer,
      onLostPointerCapture: releasePointer,
      onWheel,
    },
  };
}
